import userStore from "@/store/modules/userStore";
import { useDisplayHelpers } from "@/components/mixins/DisplayHelpers";
import type Key from "@/app/types/common/key";
import { keys } from "@/plugins/i18n";
import type { Maybe } from "@/components/utils/typeHelpers";

const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
];

export interface Address {
  addressBusinessName?: string;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  addressLine4: string;
  addressLine5: string;
  addressPostcode: string;
  addressLine6: string;
}

export interface AddressPart {
  t: Key;
  read: (address: Address) => string;
  write: (address: Address, value: string) => void;
  type: "text" | "country" | "ddl";
  isValidFor: (address: Address) => boolean;
  ddlOptions: readonly string[];
}

function addressPart(opts: {
  t: Key;
  read: (address: Address) => Maybe<string>;
  write: (address: Address, value: string) => void;
  type?: Maybe<"text" | "country" | "ddl">;
  onlyValidIfHasProp?: boolean;
  ddlOptions?: Maybe<readonly string[]>;
}): AddressPart {
  return {
    t: opts.t,
    read: (address) => opts.read(address) ?? "",
    write: opts.write,
    type: opts.type ?? "text",
    isValidFor: opts.onlyValidIfHasProp
      ? (address: Address) => opts.read(address) !== undefined
      : () => true,
    ddlOptions: opts.ddlOptions ?? [],
  };
}

export function getAddressPartDisplayOrder(): AddressPart[] {
  const businessName = addressPart({
    t: keys.address_business,
    read: (x: Address) => x.addressBusinessName,
    write: (x: Address, val: string) => (x.addressBusinessName = val),
    onlyValidIfHasProp: true,
  });

  const line1 = addressPart({
    t: keys.address_line_1,
    read: (x: Address) => x.addressLine1,
    write: (x: Address, val: string) => (x.addressLine1 = val),
  });

  const line2 = addressPart({
    t: keys.address_line_2,
    read: (x: Address) => x.addressLine2,
    write: (x: Address, val: string) => (x.addressLine2 = val),
  });

  const line3 = addressPart({
    t: keys.address_line_3,
    read: (x: Address) => x.addressLine3,
    write: (x: Address, val: string) => (x.addressLine3 = val),
  });

  const line4 = addressPart({
    t: keys.address_line_4,
    read: (x: Address) => x.addressLine4,
    write: (x: Address, val: string) => (x.addressLine4 = val),
  });

  const line5 = addressPart({
    t: keys.address_line_5,
    read: (x: Address) => x.addressLine5,
    write: (x: Address, val: string) => (x.addressLine5 = val),
  });

  const usState = addressPart({
    t: keys.address_line_5,
    read: (x: Address) => x.addressLine5,
    write: (x: Address, val: string) => (x.addressLine5 = val),
    type: "ddl",
    ddlOptions: US_STATES,
  });

  const postcode = addressPart({
    t: keys.address_postcode,
    read: (x: Address) => x.addressPostcode,
    write: (x: Address, val: string) => (x.addressPostcode = val),
  });

  const line6 = addressPart({
    t: keys.address_line_6,
    read: (x: Address) => x.addressLine6,
    write: (x: Address, val: string) => (x.addressLine6 = val),
    type: "country",
  });

  const countryCode =
    userStore.state.tenant?.locationConfiguration.locationCountryCode ?? "GB";

  return countryCode === "US"
    ? [businessName, line1, line2, line3, line4, usState, postcode, line6]
    : countryCode === "DE"
      ? [businessName, line1, line2, line3, postcode, line4, line5, line6]
      : [businessName, line1, line2, line3, line4, line5, postcode, line6];
}

export function formatAddressForDisplay(
  address: Address,
  defaultEmptyValue = "-"
) {
  const { displayString } = useDisplayHelpers({ defaultValue: "" });

  const formattedAddress = displayString(
    getAddressPartDisplayOrder()
      .map((part) => part.read(address))
      .filter((v) => !!v)
      .join(", ")
  );

  return formattedAddress === "-" ? defaultEmptyValue : formattedAddress;
}

export function cloneAddress(toClone: Address): Address {
  return {
    addressBusinessName: toClone.addressBusinessName,
    addressLine1: toClone.addressLine1,
    addressLine2: toClone.addressLine2,
    addressLine3: toClone.addressLine3,
    addressLine4: toClone.addressLine4,
    addressLine5: toClone.addressLine5,
    addressPostcode: toClone.addressPostcode,
    addressLine6: toClone.addressLine6,
  };
}
